/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from "react-router-dom";
import { getEventDetails } from "../../../firebaseFunctions/getEventDetails/getEventDetails";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";
import ModalComponent from "../../MPS/EventRegistration/EventsModal";
import eventImage from "../../../assets/images/no_image_placeholder.jpg";
import alert from "../../../assets/images/alert-circle.svg";
import PopUpModal from "../../PopUpModal/PopUpModal";
import { useAuth } from "../../../context/AuthContext";
import EventDetails from "./EventDetails";
import MarketplaceSellers from "./MarketplaceSellers";
import Announcements from "./Announcements";
import Faq from "./Faq";
import Contributors from "./Contributors";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/solid";
import EditEventDetails from "./EditEventDetails";
import { getUserDetails } from "../../../firebaseFunctions/getUserDetails/getUserDetails";
import ShareEventModal from "./ShareEventModal";
import RequiredDocuments from "./RequiredDocuments";
import Ticketing from "./Ticketing";
import FloorPlanEditor from "./FloorPlan/FloorPlanEditor";

const tabs = [
    "Event Details",
    "Marketplace Vendors",
    "Floor Plan",
    "Ticketing",
    "Required Documents",
    "Announcements",
    "FAQ",
    "Contributors",
    "Edit Event Details"
];

const ViewEventDetails = () => {
    const { t } = useTranslation();

    const tabLabels = {
        "Event Details": t('eventDetails.tabs.eventDetails'),
        "Marketplace Vendors": t('eventDetails.tabs.marketplaceVendors'),
        "Floor Plan": "Floor Plan",
        "Ticketing": t('eventDetails.tabs.ticketing'),
        "Required Documents": t('eventDetails.tabs.requiredDocuments'),
        "Announcements": t('eventDetails.tabs.announcements'),
        "FAQ": t('eventDetails.tabs.faq'),
        "Contributors": t('eventDetails.tabs.contributors'),
        "Edit Event Details": t('eventDetails.tabs.editEventDetails')
    };

    const [isLoading, setIsLoading] = useState(false)
    const [cancelReason, setCancelReason] = useState('')
    const [eventDetails, setEventDetails] = useState({})
    const [mpsRegistrations, setMpsRegistrations] = useState([])
    const [selectedTab, setSelectedTab] = useState("Event Details");
    const { id } = useParams()
    const navigate = useNavigate();
    const [popUpModalType, setPopUpModalType] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const { user } = useAuth();

    const handleCancelEventClick = () => {
        setIsModalOpen(true)
        setPopUpModalType("event cancel verification")
    };
    const handleCancelEvent = async () => {
        setIsLoading(true);
        try {
            const fns = getFunctions();
            const cancelEventFn = httpsCallable(fns, 'cancelEvent');
            const result = await cancelEventFn({ eventId: id, reason: cancelReason });
            if (result?.data?.success) {
                setPopUpModalType("event cancel success");
            } else {
                setPopUpModalType("event cancel failure");
            }
        } catch (error) {
            console.error("Cancel event error:", error);
            setPopUpModalType("event cancel failure");
        } finally {
            setIsLoading(false);
        }
    };

    // edit event tab
    const handleCancelChanges = () => {
        setIsModalOpen(true)
        setPopUpModalType("cancel edit")
    };

    const handleSaveChanges = () => {
        setIsModalOpen(true)
        setPopUpModalType("cancel edit")
    };

    const fetchPreFilledData = async () => {
        setIsLoading(true);
        try {
            const eventDetailsData = await getEventDetails(id);
            if (eventDetailsData.success) {
                setEventDetails(eventDetailsData?.data?.event);
                setMpsRegistrations(eventDetailsData?.data?.mpsRegistrations);
            } else {
                console.log("Event details not found");
            }
        } catch (error) {
            console.log("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchPreFilledData();
        }
    }, [id]);

    useEffect(() => {
        if (popUpModalType === "save changes success") {
            fetchPreFilledData();
        }
    }, [popUpModalType, id]);

    const handleBack = () => {
        navigate("/eo/events");
    };

    const fetchUserDetails = async () => {
        if (user?.uid) {
            setIsLoading(true);
            setIsModalOpen(true)
            // Ensure user and user.uid are defined
            try {
                const userDetails = await getUserDetails(user.uid); // Await the promise
                setUserDetails(userDetails);
            } catch (error) {
                console.error("Error fetching user details:", error);
            } finally {
                setIsLoading(false);
                if (!popUpModalType) {
                    setIsModalOpen(false)
                }
            }
        }
    };

    useEffect(() => {
        fetchUserDetails();
    }, [user]);

    const renderContent = () => {
        switch (selectedTab) {
            case "Event Details":
                return <EventDetails
                    data={eventDetails}
                    handleCancelEvent={handleCancelEventClick}
                    handleEditEvent={() => setSelectedTab("Edit Event Details")}
                />;
            case "Marketplace Vendors":
                return <MarketplaceSellers
                    mpsRegistrations={mpsRegistrations}
                    sellerCapacity={eventDetails?.sellerCapacity}
                    reload={fetchPreFilledData}
                />;
            case "Floor Plan":
                return <FloorPlanEditor
                    eventId={id}
                    registrations={mpsRegistrations || []}
                />;
            case "Ticketing":
                return <Ticketing
                    eventId={id}
                    eoUserId={user?.uid}
                    eventName={eventDetails?.eventName}
                />;
            case "Required Documents":
                return <RequiredDocuments
                    eventId={id}
                    organizerId={user?.uid}
                    approvedVendors={(mpsRegistrations || [])
                      .filter(r => r.applicationStatus === 'Approved' || r.applicationStatus === 'Registered')
                      .map(r => ({ id: r.mpsUserId || r.userId, vendorName: r.vendorName || r.businessName }))}
                />;
            case "Announcements":
                return <Announcements
                    announcements={eventDetails?.announcements}
                    userData={userDetails}
                    reload={fetchPreFilledData}
                    mpsRegistrations={mpsRegistrations || []}
                    eventName={eventDetails?.eventName || ''}
                />;
            case "FAQ":
                return <Faq
                    eventData={eventDetails}
                    reload={fetchPreFilledData}
                />;
            case "Contributors":
                return <Contributors userDetails={userDetails} />;
            case "Edit Event Details":
                return <EditEventDetails
                    eventData={eventDetails}
                    handleCancelChanges={handleCancelChanges}
                    handleSaveChanges={handleSaveChanges}
                    setIsLoading={setIsLoading}
                    setPopUpModalType={setPopUpModalType}
                    setIsModalOpen={setIsModalOpen}
                />
            default:
                return <></>;
        }
    };

    const renderCreateEventModal = () => {
        switch (popUpModalType) {
            case "event cancel verification":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('eventDetails.cancelEventTitle', { eventName: eventDetails?.eventName })}
                        message={t('eventDetails.cancelEventMessage')}
                        secondMessage=""
                        secondButtonLabel={t('eventDetails.yesCancelButton')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('eventDetails.noDontCancel')}
                        onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); setCancelReason(''); }}
                        onSecondButtonClick={handleCancelEvent}
                    >
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Reason for cancellation (optional)"
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-grey-300 rounded-lg resize-none text-grey-900 placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-grey-300"
                        />
                    </ModalComponent>
                );
            case "event cancel failure":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('eventDetails.unableToCancelTitle')}
                        message={t('eventDetails.unableToCancelMessage')}
                        firstButtonLabel={t('common.back')}
                        onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}

                    />
                );
            case "event cancel success":
                return (
                    <ModalComponent
                        icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
                        title={t('eventDetails.eventCancelledTitle')}
                        firstButtonLabel={t('eventDetails.goToEvents')}
                        onFirstButtonClick={handleBack}

                    />
                );
            case "cancel edit":
                return (
                    <ModalComponent
                        title={t('eventDetails.cancelChangesTitle')}
                        message={t('eventDetails.cancelChangesMessage')}
                        secondMessage=""
                        firstButtonLabel={t('eventDetails.continueEditing')}
                        onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        secondButtonLabel={t('eventDetails.leaveAnyway')}
                        secondButtonVariant="transparent"
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); setSelectedTab("Event Details") }}

                    />
                );
            case "save changes success":
                return (
                    <ModalComponent
                        icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
                        title={t('eventDetails.changesSavedTitle')}
                        firstButtonLabel={t('eventDetails.goToEventPage')}
                        onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); setSelectedTab("Event Details") }}
                        secondButtonLabel={t('eventDetails.continueEditing')}
                        secondButtonVariant="transparent"
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}

                    />
                );
            case "save changes failure":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('eventDetails.errorSavingTitle')}
                        firstButtonLabel={t('eventDetails.continueEditing')}
                        onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        secondButtonLabel={t('eventDetails.leaveAnyway')}
                        secondButtonVariant="transparent"
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); setSelectedTab("Event Details") }}

                    />
                );
            default:
                return <ModalLoadingState />;
        }
    };

    return (
        <>
            <PopUpModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                popUpModalType={popUpModalType}
            >
                {!isLoading ? renderCreateEventModal() : <ModalLoadingState />}
            </PopUpModal>
            <div className="w-full p-8">
                <div className="flex flex-col items-center justify-center w-full rounded-2xl">
                    <div className="flex items-center justify-between w-full pl-1 pr-1 sm:pr-8 sm:pl-8 ">
                        <div className="">
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-2 mb-6 text-sm font-medium underline text-grey-900 underline-offset-4"
                            >
                                <ion-icon name="chevron-back-outline" />
                                {t('common.back')}
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between w-full pl-1 pr-1 sm:pr-8 sm:pl-8">
                        <img
                            src={eventDetails?.eventCoverPhoto || eventImage}
                            alt="Event"
                            className="w-full h-60 object-cover object-middle rounded-t-xl"
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full pl-1 pr-1 sm:pr-8 py-5 sm:pl-8 gap-4">
                        <h1 className="text-2xl font-medium text-grey-900">
                            {eventDetails?.eventName}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => navigate(`/eo/events/${id}/checkin`)}
                                className="flex items-center gap-2 px-4 py-2 bg-grey-100 hover:bg-grey-200 text-grey-900 text-sm font-semibold rounded-xl transition-colors shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                </svg>
                                Attendee Check-In
                            </button>
                            <button
                                onClick={() => navigate(`/eo/events/${id}/vendor-checkin`)}
                                className="flex items-center gap-2 px-4 py-2 bg-grey-900 hover:bg-grey-800 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Vendor Check-In
                            </button>
                            <button
                                onClick={() => setShareModalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                Share with Vendors
                            </button>
                        </div>
                    </div>

                    <div className="self-stretch bg-white w-full px-4 sm:px-8 pt-2 border-b border-grey-200">
                        <div className="relative">
                        {/* Fade hint to indicate more tabs are scrollable */}
                        <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent z-10" />
                        <div className="flex flex-row items-center gap-1 overflow-x-auto pb-0 scrollbar-none pr-12">
                            {tabs.filter(tab => tab !== "Edit Event Details").map((tab, index) => {
                                const isActive = selectedTab === tab || (selectedTab === "Edit Event Details" && tab === "Event Details");
                                return (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedTab(tab)}
                                        className={`flex-shrink-0 px-3 py-2 rounded-t-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${
                                            isActive
                                                ? "bg-grey-900 text-white shadow-sm"
                                                : "text-grey-500 hover:text-grey-800 hover:bg-grey-100"
                                        }`}
                                    >
                                        {tabLabels[tab] || tab}
                                    </button>
                                );
                            })}
                        </div>
                        </div>
                    </div>
                    <div className="self-stretch bg-white flex flex-col items-start justify-start w-full">
                        {renderContent()}
                    </div>
                </div>
            </div>
            <ShareEventModal
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                event={eventDetails}
                eventId={id}
            />
        </>
    )
}

export default ViewEventDetails



